import { IonAvatar } from '@ionic/react';
import type React from 'react';

interface PlayerAvatarProps {
  image?: string | null;
  nick: string;
  size?: 'sm' | 'compact' | 'md';
}

/**
 * Extracts initials from a player nick
 * Examples:
 * - "Pablo Erasmo" -> "PE"
 * - "P. Erasmo" -> "PE"
 * - "P.Erasmo" -> "PE"
 * - "Longo" -> "L"
 * - "U. Ekeneme" -> "UE"
 */
const getInitials = (nick: string): string => {
  // Remove extra spaces and split by space or dot
  const parts = nick
    .trim()
    .split(/[\s.]+/)
    .filter(part => part.length > 0);

  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    // Single name: return first letter
    return parts[0].charAt(0).toUpperCase();
  }

  // Multiple parts: return first letter of first two parts
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

export const PlayerAvatar: React.FC<PlayerAvatarProps> = ({
  image,
  nick,
  size = 'md',
}) => {
  const initials = getInitials(nick);
  const dimension = size === 'sm' ? '28px' : size === 'compact' ? '44px' : '64px';
  const avatarStyle = { width: dimension, height: dimension, flexShrink: 0 };
  const initialsClass = size === 'md' ? 'text-lg' : size === 'compact' ? 'text-sm' : 'text-xs';

  return (
    <IonAvatar style={avatarStyle}>
      {image ? (
        <img src={image} alt={nick} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center">
          <span className={`text-white font-bold ${initialsClass}`}>
            {initials}
          </span>
        </div>
      )}
    </IonAvatar>
  );
};
